// Production cycle editor — admin tweaks the runtime config of each
// stage and its operations without redeploying.
//
// Layout: left rail = stage list, right pane = chosen stage's config:
//   - exception_signals catalogue editor (add / remove / rename)
//   - per_item_label_kind + batch_label_kind (free-form slugs that
//     match print_templates.kind)
//   - per-operation printer binding card (PRINTING-flavored ops + QC
//     ops that auto-print on accept)
// Above the stages: order-attribute formulas / defaults of each process
// (PATCH /admin/cycle/processes/:key/order-attributes).
//
// Backend contract: PATCH /admin/cycle/stages/:id + /operations/:id.
// Permission: cycle.manage.

import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ErrorBanner } from "@/components/ErrorBanner";
import { NameTranslationsEditor } from "@/components/NameTranslationsEditor";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/lib/AuthContext";
import { displayName } from "@/lib/format";
import { localized } from "@/lib/localized";
import {
  ApiError,
  adminCycleApi,
  manufacturingOrdersApi,
  printAgentsAdminApi,
  stagesApi,
  type PrintAgentRead,
} from "@/lib/api";
import type {
  ExceptionSignalDef,
  FlowDirection as FlowDirectionT,
  FlowType as FlowTypeT,
  IdentificationMethod,
  OperationConfig,
  OperationRead,
  OperationType,
  OrderAttributeDef,
  OrderProcess,
  ProcessFlowView,
  ProcessOperationView,
  StageConfig,
  StageRead,
} from "@/lib/types";
import {
  FlowDirection,
  FlowType,
  IdentificationMethod as IDENTIFICATION_METHODS,
  OperationType as OPERATION_TYPES,
} from "@/lib/types";

const INPUT = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";
const INPUT_MONO = `${INPUT} font-mono`;

// Map a mutation error to a Russian, user-facing string. 409 from the
// constructor endpoints means the entity is still referenced (orders /
// execution records) and can't be deleted — explain that instead of
// surfacing the raw backend code.
function mutationErrorText(err: unknown, conflictMsg?: string): string {
  if (err instanceof ApiError) {
    if (err.status === 409 && conflictMsg) return conflictMsg;
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

export default function CycleEditor() {
  return (
    <RequirePermission codes="cycle.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const stages = useQuery({
    queryKey: ["stages", "all-with-config"],
    queryFn: () => stagesApi.list({ page_size: 100 }),
    staleTime: 30_000,
  });

  const [selectedStageId, setSelectedStageId] = useState<number | null>(null);

  // Auto-select first stage once data loads.
  useEffect(() => {
    if (selectedStageId === null && stages.data && stages.data.items.length > 0) {
      setSelectedStageId(stages.data.items[0]!.id);
    }
  }, [stages.data, selectedStageId]);

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminCycle")}
        title={t("cycleEditor.title")}
        description={t("cycleEditor.description")}
      />

      <OrderFormulasCard />

      {stages.isLoading && <LoadingState />}
      {stages.isError && <ErrorState error={stages.error} />}

      {stages.data && (
        <div className="grid grid-cols-1 lg:grid-cols-[260px,1fr] gap-4 mt-4">
          <StageRail
            stages={stages.data.items}
            selectedId={selectedStageId}
            onSelect={setSelectedStageId}
            onStageCreated={(id) => {
              qc.invalidateQueries({ queryKey: ["stages", "all-with-config"] });
              setSelectedStageId(id);
            }}
          />
          {selectedStageId !== null && (
            <StagePane
              key={selectedStageId}
              stageId={selectedStageId}
              onStageDeleted={() => {
                qc.invalidateQueries({ queryKey: ["stages", "all-with-config"] });
                setSelectedStageId(null);
              }}
            />
          )}
        </div>
      )}
    </>
  );
}

// ---------- Left rail ------------------------------------------------------

function StageRail({
  stages,
  selectedId,
  onSelect,
  onStageCreated,
}: {
  stages: StageRead[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onStageCreated: (id: number) => void;
}) {
  const { t } = useTranslation();
  const [adding, setAdding] = useState(false);
  const [code, setCode] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [identification, setIdentification] =
    useState<IdentificationMethod>("LABEL");

  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.createStage({
        code: code.trim(),
        name_ru: nameRu.trim(),
        identification,
      }),
    onSuccess: (created) => {
      setAdding(false);
      setCode("");
      setNameRu("");
      setIdentification("LABEL");
      onStageCreated(created.id);
    },
  });

  const canSubmit = code.trim().length > 0 && nameRu.trim().length > 0;

  return (
    <nav className="card p-2 space-y-1 self-start">
      {stages.map((s) => (
        <button
          key={s.id}
          onClick={() => onSelect(s.id)}
          className={`block w-full text-left px-3 py-2 rounded-md transition-colors ${
            selectedId === s.id
              ? "bg-surface border border-accent"
              : "hover:bg-surface"
          }`}
        >
          <p className="font-mono text-xs text-text-muted">{s.code}</p>
          <p className="text-sm">{displayName(s)}</p>
        </button>
      ))}

      <div className="pt-2 border-t border-border mt-2">
        {!adding ? (
          <button
            className="btn w-full"
            onClick={() => {
              m.reset();
              setAdding(true);
            }}
          >
            {t("cycleEditor.createStage")}
          </button>
        ) : (
          <div className="space-y-2 p-1">
            <input
              className={INPUT_MONO}
              placeholder={t("cycleEditor.codePlaceholder")}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
            <input
              className={INPUT}
              placeholder={t("cycleEditor.nameRuPlaceholder")}
              value={nameRu}
              onChange={(e) => setNameRu(e.target.value)}
            />
            <select
              className={INPUT_MONO}
              value={identification}
              onChange={(e) =>
                setIdentification(e.target.value as IdentificationMethod)
              }
            >
              {IDENTIFICATION_METHODS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
            {m.isError && (
              <ErrorBanner onDismiss={() => m.reset()}>
                {mutationErrorText(
                  m.error,
                  t("cycleEditor.errStageExists"),
                )}
              </ErrorBanner>
            )}
            <div className="flex gap-2">
              <button
                className="btn btn-primary flex-1"
                disabled={!canSubmit || m.isPending}
                onClick={() => m.mutate()}
              >
                {m.isPending ? t("common.creating") : t("common.create")}
              </button>
              <button
                className="btn"
                disabled={m.isPending}
                onClick={() => {
                  setAdding(false);
                  m.reset();
                }}
              >
                {t("common.cancel")}
              </button>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

// ---------- Right pane -----------------------------------------------------

function StagePane({
  stageId,
  onStageDeleted,
}: {
  stageId: number;
  onStageDeleted: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const stage = useQuery({
    queryKey: ["stage", stageId, "detailed"],
    queryFn: () => stagesApi.detailed(stageId),
  });
  // Declarative graph — the only source for flow_specs and the
  // declarative operation fields (inputs/outputs/on_reject/…).
  const graph = useQuery({
    queryKey: ["cycle", "graph"],
    queryFn: () => adminCycleApi.getGraph(),
    staleTime: 30_000,
  });

  const del = useMutation({
    mutationFn: () => adminCycleApi.deleteStage(stageId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cycle", "graph"] });
      onStageDeleted();
    },
  });

  if (stage.isLoading) return <LoadingState />;
  if (stage.isError) return <ErrorState error={stage.error} />;
  if (!stage.data) return null;

  const cfg = (stage.data.config as StageConfig) ?? {};
  const stageGraph = graph.data?.stages.find((s) => s.id === stageId) ?? null;

  return (
    <div className="space-y-6">
      <header className="card p-4 space-y-3">
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <p className="font-mono text-xs text-text-muted">{stage.data.code}</p>
            <h2 className="text-xl mt-1">{displayName(stage.data)}</h2>
          </div>
          <button
            className="btn text-danger border-danger hover:bg-danger/10"
            disabled={del.isPending}
            onClick={() => {
              if (
                window.confirm(
                  t("cycleEditor.confirmDeleteStage", { name: displayName(stage.data!), code: stage.data!.code }),
                )
              ) {
                del.mutate();
              }
            }}
          >
            {del.isPending ? t("cycleEditor.deleting") : t("cycleEditor.deleteStage")}
          </button>
        </div>
        <div className="pt-3 border-t border-border">
          <NameTranslationsEditor
            nameKey={stageGraph?.name_key}
            names={stageGraph?.names}
          />
        </div>
      </header>

      {del.isError && (
        <ErrorBanner onDismiss={() => del.reset()}>
          {mutationErrorText(
            del.error,
            t("cycleEditor.errStageHasOrders"),
          )}
        </ErrorBanner>
      )}

      <LabelKindsCard
        stageId={stageId}
        initial={{
          per_item_label_kind: cfg.per_item_label_kind ?? null,
          batch_label_kind: cfg.batch_label_kind ?? null,
        }}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <ExceptionSignalsCard
        stageId={stageId}
        initial={cfg.exception_signals ?? []}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <MaterialConsumptionCard
        stageId={stageId}
        initial={cfg.material_consumption ?? {}}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <QcChecklistCard
        stageId={stageId}
        initial={cfg.qc_checklist ?? []}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <MaterialsHintCard
        stageId={stageId}
        initial={cfg.materials_hint ?? ""}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <ShortfallReasonsCard
        stageId={stageId}
        initial={cfg.shortfall_reasons ?? []}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
      />

      <OperationsCard
        stageId={stageId}
        operations={stage.data.operations}
        graphOperations={stageGraph?.operations ?? []}
        onSaved={() => qc.invalidateQueries({ queryKey: ["stage", stageId] })}
        onGraphChanged={() => {
          qc.invalidateQueries({ queryKey: ["stage", stageId] });
          qc.invalidateQueries({ queryKey: ["cycle", "graph"] });
        }}
      />

      <FlowsCard
        stageId={stageId}
        flows={stageGraph?.flows ?? []}
        loading={graph.isLoading}
        error={graph.isError ? graph.error : null}
        onGraphChanged={() =>
          qc.invalidateQueries({ queryKey: ["cycle", "graph"] })
        }
      />
    </div>
  );
}

// ---------- Cards ---------------------------------------------------------

function LabelKindsCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: { per_item_label_kind: string | null; batch_label_kind: string | null };
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [perItem, setPerItem] = useState(initial.per_item_label_kind ?? "");
  const [batch, setBatch] = useState(initial.batch_label_kind ?? "");

  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.patchStage(stageId, {
        per_item_label_kind: perItem.trim() || null,
        batch_label_kind: batch.trim() || null,
      }),
    onSuccess: onSaved,
  });

  const dirty =
    (perItem || "") !== (initial.per_item_label_kind ?? "") ||
    (batch || "") !== (initial.batch_label_kind ?? "");

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.autoPrintTemplates")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.autoPrintHint1")} <code className="font-mono">print_templates.kind</code>.
        {t("cycleEditor.autoPrintHint2")}
      </p>
      <Field label={t("cycleEditor.perItemKind")} hint={t("cycleEditor.perItemKindHint")}>
        <input
          className={INPUT_MONO}
          placeholder={t("cycleEditor.perItemKindPlaceholder")}
          value={perItem}
          onChange={(e) => setPerItem(e.target.value)}
        />
      </Field>
      <Field label={t("cycleEditor.batchKind")} hint={t("cycleEditor.batchKindHint")}>
        <input
          className={INPUT_MONO}
          placeholder={t("cycleEditor.batchKindPlaceholder")}
          value={batch}
          onChange={(e) => setBatch(e.target.value)}
        />
      </Field>
      {m.isError && (
        <ErrorBanner onDismiss={() => m.reset()}>
          {(m.error as Error).message}
        </ErrorBanner>
      )}
      <button
        className="btn btn-primary"
        disabled={!dirty || m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("common.save")}
      </button>
    </section>
  );
}

function ExceptionSignalsCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: ExceptionSignalDef[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<ExceptionSignalDef[]>(initial);

  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.patchStage(stageId, { exception_signals: list }),
    onSuccess: onSaved,
  });

  function patch(i: number, k: keyof ExceptionSignalDef, v: string) {
    setList((cur) => cur.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  }
  function remove(i: number) {
    setList((cur) => cur.filter((_, idx) => idx !== i));
  }
  function add() {
    setList((cur) => [...cur, { code: "", label_ru: "" }]);
  }

  const dirty = useMemo(
    () => JSON.stringify(list) !== JSON.stringify(initial),
    [list, initial],
  );

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.defectReasons")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.defectReasonsHint")}
      </p>

      <table className="w-full text-sm">
        <thead className="text-text-muted text-xs">
          <tr>
            <th className="text-left px-2 py-1 w-1/3">code</th>
            <th className="text-left px-2 py-1">{t("cycleEditor.colName")}</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((s, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1">
                <input
                  className={INPUT_MONO}
                  value={s.code}
                  onChange={(e) => patch(i, "code", e.target.value.toUpperCase())}
                />
              </td>
              <td className="px-2 py-1">
                <input
                  className={INPUT}
                  value={s.label_ru}
                  onChange={(e) => patch(i, "label_ru", e.target.value)}
                />
              </td>
              <td className="px-2 py-1 text-right">
                <button
                  className="text-text-muted hover:text-danger"
                  onClick={() => remove(i)}
                  title={t("cycleEditor.deleteTitle")}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr>
              <td colSpan={3} className="px-2 py-3 text-center text-text-muted text-xs">
                {t("cycleEditor.defectReasonsEmpty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center gap-2">
        <button className="btn" onClick={add}>{t("cycleEditor.addReason")}</button>
        {m.isError && (
          <span className="text-danger text-sm">{(m.error as Error).message}</span>
        )}
        <span className="flex-1" />
        <button
          className="btn btn-primary"
          disabled={!dirty || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  );
}

function MaterialConsumptionCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: Record<string, number>;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  type Row = { code: string; qty: string };
  const initialRows = (): Row[] =>
    Object.entries(initial).map(([code, qty]) => ({
      code,
      qty: String(qty),
    }));
  const [list, setList] = useState<Row[]>(initialRows());

  function patch(i: number, k: keyof Row, v: string) {
    setList((cur) => cur.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  }
  function remove(i: number) {
    setList((cur) => cur.filter((_, idx) => idx !== i));
  }
  function add() {
    setList((cur) => [...cur, { code: "", qty: "" }]);
  }

  const toPayload = (): Record<string, number> | null => {
    const out: Record<string, number> = {};
    for (const r of list) {
      const c = r.code.trim();
      if (!c) continue;
      const q = Number(r.qty);
      if (!Number.isFinite(q) || q <= 0) continue;
      out[c] = q;
    }
    return Object.keys(out).length ? out : null;
  };

  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.patchStage(stageId, {
        material_consumption: toPayload(),
      }),
    onSuccess: onSaved,
  });

  const dirty = useMemo(
    () => JSON.stringify(toPayload() ?? {}) !== JSON.stringify(initial ?? {}),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, initial],
  );

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.materialConsumption")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.materialConsumptionHint1")} <code className="font-mono">+1</code> {t("cycleEditor.materialConsumptionHint2")}
        <code className="font-mono">produce</code>{t("cycleEditor.materialConsumptionHint3")}
        <a className="text-accent-strong" href="/admin/inventory">{t("cycleEditor.catalog")}</a> {t("cycleEditor.materialConsumptionHint4")} <code className="font-mono">MS</code>, <code className="font-mono">FS</code>, <code className="font-mono">FST</code>). {t("cycleEditor.materialConsumptionHint5")} <code className="font-mono">produce</code> {t("cycleEditor.materialConsumptionHint6")}
      </p>

      <table className="w-full text-sm">
        <thead className="text-text-muted text-xs">
          <tr>
            <th className="text-left px-2 py-1 w-1/3">material.code</th>
            <th className="text-left px-2 py-1">{t("cycleEditor.qtyPerUnit")}</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((r, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1">
                <input
                  className={INPUT_MONO}
                  value={r.code}
                  onChange={(e) => patch(i, "code", e.target.value.toUpperCase())}
                  placeholder="MS"
                />
              </td>
              <td className="px-2 py-1">
                <input
                  type="number"
                  min={0}
                  step={0.001}
                  className={INPUT_MONO}
                  value={r.qty}
                  onChange={(e) => patch(i, "qty", e.target.value)}
                  placeholder="42"
                />
              </td>
              <td className="px-2 py-1 text-right">
                <button
                  className="text-text-muted hover:text-danger"
                  onClick={() => remove(i)}
                  title={t("cycleEditor.deleteTitle")}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr>
              <td colSpan={3} className="px-2 py-3 text-center text-text-muted text-xs">
                {t("cycleEditor.materialConsumptionEmpty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center gap-2">
        <button className="btn" onClick={add}>{t("cycleEditor.addMaterial")}</button>
        {m.isError && (
          <span className="text-danger text-sm">{(m.error as Error).message}</span>
        )}
        <span className="flex-1" />
        <button
          className="btn btn-primary"
          disabled={!dirty || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  );
}

// Domain wording for the mobile operator screens — kept in stage.config
// so a different production process supplies its own without touching
// mobile/src/pages (см. docs/adr/ADR-008-declarative-process-engine.md).
function QcChecklistCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: { text: string }[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<{ text: string }[]>(initial);

  const toPayload = (): { text: string }[] | null => {
    const out = list.map((x) => ({ text: x.text.trim() })).filter((x) => x.text);
    return out.length ? out : null;
  };

  const m = useMutation({
    mutationFn: () => adminCycleApi.patchStage(stageId, { qc_checklist: toPayload() }),
    onSuccess: onSaved,
  });

  const dirty = useMemo(
    () => JSON.stringify(toPayload() ?? []) !== JSON.stringify(initial ?? []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [list, initial],
  );

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.qcChecklist")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.qcChecklistHint")}
      </p>
      <ul className="space-y-2">
        {list.map((line, i) => (
          <li key={i} className="flex items-center gap-2">
            <input
              className={INPUT}
              value={line.text}
              placeholder={t("cycleEditor.qcChecklistPlaceholder")}
              onChange={(e) =>
                setList((cur) => cur.map((x, idx) => (idx === i ? { text: e.target.value } : x)))
              }
            />
            <button
              className="text-text-muted hover:text-danger px-2"
              onClick={() => setList((cur) => cur.filter((_, idx) => idx !== i))}
              title={t("cycleEditor.deleteTitle")}
            >
              ×
            </button>
          </li>
        ))}
        {list.length === 0 && (
          <li className="text-text-muted text-xs">{t("cycleEditor.qcChecklistEmpty")}</li>
        )}
      </ul>
      <div className="flex items-center gap-2">
        <button className="btn" onClick={() => setList((cur) => [...cur, { text: "" }])}>
          {t("cycleEditor.addItem")}
        </button>
        {m.isError && (
          <span className="text-danger text-sm">{(m.error as Error).message}</span>
        )}
        <span className="flex-1" />
        <button
          className="btn btn-primary"
          disabled={!dirty || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  );
}

function MaterialsHintCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: string;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [hint, setHint] = useState(initial);
  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.patchStage(stageId, { materials_hint: hint.trim() || null }),
    onSuccess: onSaved,
  });
  const dirty = (hint || "") !== (initial ?? "");

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.materialsHint")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.materialsHintDesc")}
      </p>
      <textarea
        className={`${INPUT} min-h-[72px]`}
        value={hint}
        placeholder={t("cycleEditor.materialsHintPlaceholder")}
        onChange={(e) => setHint(e.target.value)}
      />
      {m.isError && (
        <ErrorBanner onDismiss={() => m.reset()}>{(m.error as Error).message}</ErrorBanner>
      )}
      <button
        className="btn btn-primary"
        disabled={!dirty || m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("common.save")}
      </button>
    </section>
  );
}

function ShortfallReasonsCard({
  stageId,
  initial,
  onSaved,
}: {
  stageId: number;
  initial: ExceptionSignalDef[];
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const [list, setList] = useState<ExceptionSignalDef[]>(initial);

  function patch(i: number, k: keyof ExceptionSignalDef, v: string) {
    setList((cur) => cur.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  }

  const m = useMutation({
    mutationFn: () => {
      const out = list
        .map((x) => ({ code: x.code.trim(), label_ru: x.label_ru.trim() }))
        .filter((x) => x.code && x.label_ru);
      return adminCycleApi.patchStage(stageId, { shortfall_reasons: out.length ? out : null });
    },
    onSuccess: onSaved,
  });

  const dirty = useMemo(
    () => JSON.stringify(list) !== JSON.stringify(initial),
    [list, initial],
  );

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.shortfallReasons")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.shortfallReasonsHint")}
      </p>
      <table className="w-full text-sm">
        <thead className="text-text-muted text-xs">
          <tr>
            <th className="text-left px-2 py-1 w-1/3">code</th>
            <th className="text-left px-2 py-1">{t("cycleEditor.colName")}</th>
            <th className="w-10"></th>
          </tr>
        </thead>
        <tbody>
          {list.map((s, i) => (
            <tr key={i} className="border-t border-border">
              <td className="px-2 py-1">
                <input
                  className={INPUT_MONO}
                  value={s.code}
                  onChange={(e) => patch(i, "code", e.target.value.toUpperCase())}
                />
              </td>
              <td className="px-2 py-1">
                <input
                  className={INPUT}
                  value={s.label_ru}
                  onChange={(e) => patch(i, "label_ru", e.target.value)}
                />
              </td>
              <td className="px-2 py-1 text-right">
                <button
                  className="text-text-muted hover:text-danger"
                  onClick={() => setList((cur) => cur.filter((_, idx) => idx !== i))}
                  title={t("cycleEditor.deleteTitle")}
                >
                  ×
                </button>
              </td>
            </tr>
          ))}
          {list.length === 0 && (
            <tr>
              <td colSpan={3} className="px-2 py-3 text-center text-text-muted text-xs">
                {t("cycleEditor.shortfallReasonsEmpty")}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="flex items-center gap-2">
        <button
          className="btn"
          onClick={() => setList((cur) => [...cur, { code: "", label_ru: "" }])}
        >
          {t("cycleEditor.addReason")}
        </button>
        {m.isError && (
          <span className="text-danger text-sm">{(m.error as Error).message}</span>
        )}
        <span className="flex-1" />
        <button
          className="btn btn-primary"
          disabled={!dirty || m.isPending}
          onClick={() => m.mutate()}
        >
          {m.isPending ? t("common.saving") : t("common.save")}
        </button>
      </div>
    </section>
  );
}

function OperationsCard({
  stageId,
  operations,
  graphOperations,
  onSaved,
  onGraphChanged,
}: {
  stageId: number;
  operations: OperationRead[];
  graphOperations: ProcessOperationView[];
  onSaved: () => void;
  onGraphChanged: () => void;
}) {
  const { t } = useTranslation();
  const agents = useQuery({
    queryKey: ["admin", "print-agents", "list"],
    queryFn: () => printAgentsAdminApi.list(),
    staleTime: 30_000,
  });

  // --- create-operation form ---
  const [code, setCode] = useState("");
  const [nameRu, setNameRu] = useState("");
  const [type, setType] = useState<OperationType>("PRODUCTION");

  const create = useMutation({
    mutationFn: () =>
      adminCycleApi.createOperation({
        stage_id: stageId,
        code: code.trim(),
        name_ru: nameRu.trim(),
        type,
      }),
    onSuccess: () => {
      setCode("");
      setNameRu("");
      setType("PRODUCTION");
      onGraphChanged();
    },
  });

  const canCreate = code.trim().length > 0 && nameRu.trim().length > 0;

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.operations")}</h3>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.operationsHint")}
      </p>

      <ul className="space-y-2">
        {operations.map((op) => {
          const gop = graphOperations.find((g) => g.id === op.id);
          return (
            <OperationRow
              key={op.id}
              op={op}
              nameKey={gop?.name_key}
              names={gop?.names}
              agents={agents.data?.items ?? []}
              onSaved={onSaved}
              onDeleted={onGraphChanged}
            />
          );
        })}
        {operations.length === 0 && (
          <li className="text-text-muted text-xs">{t("cycleEditor.noOperations")}</li>
        )}
      </ul>

      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-mono uppercase tracking-wider text-text-muted">
          {t("cycleEditor.newOperation")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 items-end">
          <Field label="code">
            <input
              className={INPUT_MONO}
              value={code}
              placeholder={t("cycleEditor.opCodePlaceholder")}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
            />
          </Field>
          <Field label={t("cycleEditor.nameRu")}>
            <input
              className={INPUT}
              value={nameRu}
              placeholder={t("cycleEditor.opNamePlaceholder")}
              onChange={(e) => setNameRu(e.target.value)}
            />
          </Field>
          <Field label={t("cycleEditor.opType")}>
            <select
              className={INPUT_MONO}
              value={type}
              onChange={(e) => setType(e.target.value as OperationType)}
            >
              {OPERATION_TYPES.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
        </div>
        {create.isError && (
          <ErrorBanner onDismiss={() => create.reset()}>
            {mutationErrorText(
              create.error,
              t("cycleEditor.errOpExists"),
            )}
          </ErrorBanner>
        )}
        <button
          className="btn btn-primary"
          disabled={!canCreate || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t("common.creating") : t("cycleEditor.addOperation")}
        </button>
      </div>
    </section>
  );
}

function OperationRow({
  op,
  nameKey,
  names,
  agents,
  onSaved,
  onDeleted,
}: {
  op: OperationRead;
  nameKey?: string | null;
  names?: import("@/lib/types").LocaleNames;
  agents: PrintAgentRead[];
  onSaved: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const del = useMutation({
    mutationFn: () => adminCycleApi.deleteOperation(op.id),
    onSuccess: onDeleted,
  });
  const cfg = (op.config as OperationConfig) ?? {};
  const print = cfg.print ?? {};
  const [nameRu, setNameRu] = useState<string>(op.name_ru);
  const [agentId, setAgentId] = useState<string>(print.default_agent_id ?? "");
  const [printerName, setPrinterName] = useState<string>(print.default_printer_name ?? "");

  const currentAgent = agents.find((a) => a.id === agentId);
  const printers: { name: string }[] = currentAgent?.printers ?? [];

  const m = useMutation({
    mutationFn: () => {
      const patch: import("@/lib/types").OperationConfigPatch = {
        print: {
          default_agent_id: agentId || null,
          default_printer_name: printerName || null,
        },
      };
      if (nameRu.trim() && nameRu.trim() !== op.name_ru) {
        patch.name_ru = nameRu.trim();
      }
      return adminCycleApi.patchOperation(op.id, patch);
    },
    onSuccess: onSaved,
  });

  const dirty =
    nameRu.trim() !== op.name_ru ||
    agentId !== (print.default_agent_id ?? "") ||
    printerName !== (print.default_printer_name ?? "");

  // If agent changes, printer might no longer be valid — reset.
  useEffect(() => {
    if (currentAgent && printerName && !printers.find((p: { name: string }) => p.name === printerName)) {
      setPrinterName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId]);

  return (
    <li className="border border-border rounded-md p-3 space-y-3">
      <div className="flex justify-end">
        <button
          className="text-text-muted hover:text-danger text-xs"
          disabled={del.isPending}
          onClick={() => {
            if (
              window.confirm(
                t("cycleEditor.confirmDeleteOp", { name: op.name_ru, code: op.code }),
              )
            ) {
              del.mutate();
            }
          }}
          title={t("cycleEditor.deleteOpTitle")}
        >
          {del.isPending ? t("cycleEditor.deletingShort") : t("cycleEditor.deleteShort")}
        </button>
      </div>
      {del.isError && (
        <ErrorBanner onDismiss={() => del.reset()}>
          {mutationErrorText(
            del.error,
            t("cycleEditor.errOpHasRecords"),
          )}
        </ErrorBanner>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-[140px,1fr] gap-3 items-end">
        <div>
          <p className="font-mono text-xs text-text-muted">{op.code}</p>
          <p className="text-xs">
            <span className="pill text-xs">{op.type}</span>
          </p>
        </div>
        <Field label={t("cycleEditor.nameRu")}>
          <input
            className={INPUT}
            value={nameRu}
            onChange={(e) => setNameRu(e.target.value)}
            placeholder={t("cycleEditor.opNameRuPlaceholder")}
          />
        </Field>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr,auto] gap-2 items-end">
      <Field label={t("cycleEditor.printAgent")}>
        <select
          className={INPUT_MONO}
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
        >
          <option value="">{t("cycleEditor.fromTemplate")}</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label={t("cycleEditor.printer")}>
        <select
          className={INPUT_MONO}
          value={printerName}
          onChange={(e) => setPrinterName(e.target.value)}
          disabled={!agentId}
        >
          <option value="">{t("cycleEditor.fromTemplate")}</option>
          {printers.map((p) => (
            <option key={p.name} value={p.name}>
              {p.name}
            </option>
          ))}
        </select>
      </Field>
      <button
        className="btn btn-primary"
        disabled={!dirty || m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? "…" : t("common.save")}
      </button>
      </div>
      {m.isError && (
        <p className="text-danger text-xs">{(m.error as Error).message}</p>
      )}
      <div className="pt-3 border-t border-border">
        <NameTranslationsEditor nameKey={nameKey} names={names} />
      </div>
    </li>
  );
}

// ---------- Flow specs (declarative process graph) ------------------------

function FlowsCard({
  stageId,
  flows,
  loading,
  error,
  onGraphChanged,
}: {
  stageId: number;
  flows: ProcessFlowView[];
  loading: boolean;
  error: unknown;
  onGraphChanged: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [direction, setDirection] = useState<FlowDirectionT>("INPUT");
  const [flowType, setFlowType] = useState<FlowTypeT>("INCOMING");
  const [endpoint, setEndpoint] = useState("");
  const [nameRu, setNameRu] = useState("");

  const create = useMutation({
    mutationFn: () =>
      adminCycleApi.createFlow({
        stage_id: stageId,
        code: code.trim(),
        direction,
        flow_type: flowType,
        endpoint: endpoint.trim() || null,
        name_ru: nameRu.trim() || null,
      }),
    onSuccess: () => {
      setCode("");
      setEndpoint("");
      setNameRu("");
      setDirection("INPUT");
      setFlowType("INCOMING");
      onGraphChanged();
    },
  });

  const del = useMutation({
    mutationFn: (flowId: string) => adminCycleApi.deleteFlow(flowId),
    onSuccess: onGraphChanged,
  });

  const canCreate = code.trim().length > 0;

  return (
    <section className="card p-4 space-y-3">
      <h3 className="text-base">{t("cycleEditor.flowsTitle")}</h3>
      <p className="text-text-muted text-xs">{t("cycleEditor.flowsDesc")}</p>

      {loading && <LoadingState />}
      {error != null && <ErrorBanner>{mutationErrorText(error)}</ErrorBanner>}

      {!loading && (
        <table className="w-full text-sm">
          <thead className="text-text-muted text-xs">
            <tr>
              <th className="text-left px-2 py-1">code</th>
              <th className="text-left px-2 py-1">direction</th>
              <th className="text-left px-2 py-1">flow_type</th>
              <th className="text-left px-2 py-1">endpoint</th>
              <th className="text-left px-2 py-1">{t("cycleEditor.colName")}</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {flows.map((f) => (
              <tr key={f.id} className="border-t border-border">
                <td className="px-2 py-1 font-mono text-xs">{f.code}</td>
                <td className="px-2 py-1">{f.direction}</td>
                <td className="px-2 py-1">{f.flow_type}</td>
                <td className="px-2 py-1 font-mono text-xs">{f.endpoint ?? "—"}</td>
                <td className="px-2 py-1">{f.name ?? f.name_ru ?? "—"}</td>
                <td className="px-2 py-1 text-right">
                  <button
                    className="text-text-muted hover:text-danger"
                    disabled={del.isPending}
                    onClick={() => {
                      if (
                        window.confirm(t("cycleEditor.confirmDeleteFlow", { code: f.code }))
                      ) {
                        del.mutate(f.id);
                      }
                    }}
                    title={t("cycleEditor.deleteFlowTitle")}
                  >
                    ×
                  </button>
                </td>
              </tr>
            ))}
            {flows.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  className="px-2 py-3 text-center text-text-muted text-xs"
                >
                  {t("cycleEditor.flowsEmpty")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      {del.isError && (
        <ErrorBanner onDismiss={() => del.reset()}>
          {mutationErrorText(del.error)}
        </ErrorBanner>
      )}

      <div className="border-t border-border pt-3 space-y-2">
        <p className="text-xs font-mono uppercase tracking-wider text-text-muted">
          {t("cycleEditor.newFlow")}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          <Field label="code">
            <input
              className={INPUT_MONO}
              value={code}
              placeholder={t("cycleEditor.phCode")}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
          <Field label="direction">
            <select
              className={INPUT_MONO}
              value={direction}
              onChange={(e) => setDirection(e.target.value as FlowDirectionT)}
            >
              {FlowDirection.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label="flow_type">
            <select
              className={INPUT_MONO}
              value={flowType}
              onChange={(e) => setFlowType(e.target.value as FlowTypeT)}
            >
              {FlowType.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("cycleEditor.endpointOpt")}>
            <input
              className={INPUT_MONO}
              value={endpoint}
              placeholder={t("cycleEditor.phEndpoint")}
              onChange={(e) => setEndpoint(e.target.value)}
            />
          </Field>
          <Field label={t("cycleEditor.nameOpt")}>
            <input
              className={INPUT}
              value={nameRu}
              placeholder={t("cycleEditor.phName")}
              onChange={(e) => setNameRu(e.target.value)}
            />
          </Field>
        </div>
        {create.isError && (
          <ErrorBanner onDismiss={() => create.reset()}>
            {mutationErrorText(
              create.error,
              t("cycleEditor.flowCodeExists"),
            )}
          </ErrorBanner>
        )}
        <button
          className="btn btn-primary"
          disabled={!canCreate || create.isPending}
          onClick={() => create.mutate()}
        >
          {create.isPending ? t("common.creating") : t("cycleEditor.addFlow")}
        </button>
      </div>
    </section>
  );
}

// ---------- Order attribute formulas (per process) ------------------------

function OrderFormulasCard() {
  const { t } = useTranslation();
  const { hasPermission } = useAuth();
  const canRead = hasPermission("manufacturing_order.read");
  const processes = useQuery({
    queryKey: ["manufacturing-orders", "processes"],
    queryFn: () => manufacturingOrdersApi.processes(),
    staleTime: 60_000,
    enabled: canRead,
  });
  const [processKey, setProcessKey] = useState<string | null>(null);

  const list = processes.data ?? [];
  if (!canRead || processes.isLoading || (processes.isSuccess && list.length === 0)) return null;
  const process = list.find((p) => p.key === processKey) ?? list[0];

  return (
    <section className="card p-4 space-y-3 mt-4">
      <h3 className="text-base">{t("cycleEditor.formulasTitle")}</h3>
      <p className="text-text-muted text-xs">{t("cycleEditor.formulasHint")}</p>
      {processes.isError && <ErrorBanner>{mutationErrorText(processes.error)}</ErrorBanner>}
      {list.length > 1 && (
        <div className="flex flex-wrap gap-2">
          {list.map((p) => (
            <button
              key={p.key}
              type="button"
              onClick={() => setProcessKey(p.key)}
              className={`px-3 py-2 rounded-md border text-sm ${
                p.key === process?.key
                  ? "border-accent bg-accent/10 text-accent-strong"
                  : "border-border bg-surface text-text-secondary"
              }`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}
      {process && (
        // Remount after save/refetch so the inputs show what the server stored.
        <OrderFormulasTable key={`${process.key}:${JSON.stringify(process.order.attributes)}`} process={process} />
      )}
    </section>
  );
}

function OrderFormulasTable({ process }: { process: OrderProcess }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const attrs = process.order.attributes.filter((d) => !d.computed && d.type !== "boolean");
  const numeric = (d: OrderAttributeDef) => d.type === "number" || d.type === "integer";
  const stored = (d: OrderAttributeDef) => ({
    formula: d.formula ?? "",
    def: d.default == null ? "" : String(d.default),
  });
  const [rows, setRows] = useState<Record<string, { formula: string; def: string }>>(() =>
    Object.fromEntries(attrs.map((d) => [d.key, stored(d)])),
  );
  const changed = attrs.filter((d) => {
    const r = rows[d.key]!;
    return r.formula !== stored(d).formula || r.def !== stored(d).def;
  });

  const m = useMutation({
    mutationFn: () =>
      adminCycleApi.patchOrderAttributes(process.key, {
        attributes: changed.map((d) => {
          const r = rows[d.key]!;
          const def = r.def.trim();
          return {
            key: d.key,
            formula: r.formula.trim() || null,
            default: def === "" ? null : numeric(d) ? Number(def) : def,
          };
        }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["manufacturing-orders", "processes"] }),
  });

  function patch(key: string, field: "formula" | "def", v: string) {
    setRows((cur) => ({ ...cur, [key]: { ...cur[key]!, [field]: v } }));
  }

  if (attrs.length === 0) {
    return <p className="text-text-muted text-xs">{t("cycleEditor.formulasEmpty")}</p>;
  }

  return (
    <>
      <table className="w-full text-sm">
        <thead className="text-text-muted text-xs">
          <tr>
            <th className="text-left px-2 py-1 w-1/4">{t("cycleEditor.colAttribute")}</th>
            <th className="text-left px-2 py-1">{t("cycleEditor.colFormula")}</th>
            <th className="text-left px-2 py-1 w-40">{t("cycleEditor.colDefault")}</th>
          </tr>
        </thead>
        <tbody>
          {attrs.map((d) => (
            <tr key={d.key} className="border-t border-border align-top">
              <td className="px-2 py-1">
                <p>
                  {localized(d.label, d.key)}
                  {d.unit ? `, ${d.unit}` : ""}
                </p>
                <p className="font-mono text-xs text-text-muted">{d.key}</p>
              </td>
              <td className="px-2 py-1">
                {numeric(d) ? (
                  <input
                    className={INPUT_MONO}
                    value={rows[d.key]!.formula}
                    placeholder={t("cycleEditor.formulaPlaceholder")}
                    onChange={(e) => patch(d.key, "formula", e.target.value)}
                  />
                ) : (
                  <span className="text-text-muted text-xs">—</span>
                )}
              </td>
              <td className="px-2 py-1">
                <input
                  type={numeric(d) ? "number" : d.type === "date" ? "date" : "text"}
                  step={numeric(d) ? "any" : undefined}
                  className={INPUT_MONO}
                  value={rows[d.key]!.def}
                  onChange={(e) => patch(d.key, "def", e.target.value)}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-text-muted text-xs">
        {t("cycleEditor.formulaSyntax")}{" "}
        <span className="font-mono">
          {[...process.order.attributes.map((d) => d.key), "element_count"].join(", ")}
        </span>
      </p>
      {m.isError && <ErrorBanner onDismiss={() => m.reset()}>{mutationErrorText(m.error)}</ErrorBanner>}
      <button
        className="btn btn-primary"
        disabled={changed.length === 0 || m.isPending}
        onClick={() => m.mutate()}
      >
        {m.isPending ? t("common.saving") : t("common.save")}
      </button>
    </>
  );
}

// ---------- atoms ---------------------------------------------------------

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">
        {label}
      </span>
      {hint && <span className="block text-text-muted text-xs italic mt-0.5">{hint}</span>}
      <div className="mt-1">{children}</div>
    </label>
  );
}
